import { enqueueActivityEvent, isActivityQueueEnabled } from 'services/activityTracking/activityEventQueue';
import { getProducerSessionId, nextEventId, nextProducerSequence } from 'services/activityTracking/session';
import { ACTIVITY_END_REASON, ACTIVITY_EVENT_TYPE, ACTIVITY_INPUT_SOURCE, ACTIVITY_VISIBILITY_STATE } from 'utils/activityTracking/constants';
import { buildActivityEvent } from 'utils/activityTracking/envelope';
import { toSchemaFieldKey } from 'utils/activityTracking/fieldKey';

/**
 * The focus period state machine (BEAST-13036).
 *
 * ONE STATE MACHINE, ONE OWNER. Focus periods for tabs, split view and fields are one problem, so
 * all of it lives here and the seams (the tab saga, the split view saga, the field listener, the
 * lifecycle listeners) only call in. Three independent trackers would drift and produce overlapping
 * periods.
 *
 * The invariants it exists to hold:
 *
 * - At most ONE open tab period and ONE open field period per window. The open period is always
 *   closed before the next one opens, and the closing event gets the LOWER `producer_sequence`.
 * - A field period is always nested inside a tab period for the SAME `work_object_id`. A field
 *   focus in a different transaction closes the current tab period and opens the new one FIRST.
 *   That is what makes the split-view keyboard path (tabbing from one card's form into the next
 *   without a click) correct by construction rather than a special case.
 * - Periods are NEVER merged, coalesced, deduplicated or overwritten. Returning to a transaction
 *   opens a genuinely new period with a new `correlation_id`.
 * - `end_reason` is PASSED IN, never inferred here. By the time a close reaches this module the
 *   store has already moved on, so guessing from state would be guessing wrong.
 * - Events are created EAGERLY and sent lazily: `event_id`, `occurred_at` and `producer_sequence`
 *   are stamped at the moment the interaction happens, never at send time.
 *
 * FAIL OPEN. Every entry point is wrapped: no tracking failure may block the agent, surface a
 * snackbar, or throw into a render path. Telemetry breaking has to be invisible.
 */

/** `{ correlationId, workObjectId, pageKey, context }` or null. */
let currentTabPeriod = null;

/** `{ correlationId, workObjectId, fieldKey, fieldType, pageKey, context, dirty }` or null. */
let currentFieldPeriod = null;

function visibilityState() {
    if (typeof document === 'undefined') return ACTIVITY_VISIBILITY_STATE.VISIBLE;
    return document.hidden ? ACTIVITY_VISIBILITY_STATE.HIDDEN : ACTIVITY_VISIBILITY_STATE.VISIBLE;
}

/** Context is captured onto the period when it opens, so a closing event still carries it even if
 *  the transaction has already been removed from the store by then. */
function normalizeContext(context) {
    return {
        locationId: context?.locationId ?? null,
        gateId: context?.gateId ?? null,
        deskId: context?.deskId ?? null,
    };
}

function emit(intent) {
    enqueueActivityEvent(
        buildActivityEvent({
            eventId: nextEventId(),
            occurredAt: new Date().toISOString(),
            producerSessionId: getProducerSessionId(),
            producerSequence: nextProducerSequence(),
            ...intent,
        })
    );
}

function emitTabStarted(period) {
    emit({
        eventType: ACTIVITY_EVENT_TYPE.WORK_OBJECT_FOCUS_STARTED,
        workObjectId: period.workObjectId,
        // For a tab the target IS the work object, so `target_id` repeats the transaction id.
        targetId: period.workObjectId,
        correlationId: period.correlationId,
        pageKey: period.pageKey,
        visibilityState: visibilityState(),
        ...period.context,
    });
}

function emitTabEnded(period, endReason) {
    emit({
        eventType: ACTIVITY_EVENT_TYPE.WORK_OBJECT_FOCUS_ENDED,
        workObjectId: period.workObjectId,
        targetId: period.workObjectId,
        correlationId: period.correlationId,
        endReason,
        pageKey: period.pageKey,
        visibilityState: visibilityState(),
        ...period.context,
    });
}

function emitFieldStarted(period) {
    emit({
        eventType: ACTIVITY_EVENT_TYPE.FIELD_FOCUS_STARTED,
        workObjectId: period.workObjectId,
        targetId: period.fieldKey,
        correlationId: period.correlationId,
        fieldKey: period.fieldKey,
        fieldType: period.fieldType,
        ...period.context,
    });
}

function emitFieldEnded(period, endReason) {
    emit({
        eventType: ACTIVITY_EVENT_TYPE.FIELD_FOCUS_ENDED,
        workObjectId: period.workObjectId,
        targetId: period.fieldKey,
        correlationId: period.correlationId,
        endReason,
        fieldKey: period.fieldKey,
        fieldType: period.fieldType,
        ...period.context,
    });
}

function emitValueApplied({ workObjectId, fieldKey, fieldType, inputSource, automationSource, context }) {
    emit({
        eventType: ACTIVITY_EVENT_TYPE.FIELD_VALUE_APPLIED,
        workObjectId,
        targetId: fieldKey,
        // A point event, not a period: a `correlation_id` here would invent a period that does not
        // exist, and it is not an `…ended` type so `end_reason` stays null too.
        correlationId: null,
        fieldKey,
        fieldType,
        inputSource,
        automationSource,
        ...normalizeContext(context),
    });
}

/** Closes the open field period, emitting the manual value event first when the agent changed it. */
function closeOpenFieldPeriod(endReason) {
    if (!currentFieldPeriod) return;

    const period = currentFieldPeriod;
    currentFieldPeriod = null;

    // Blur is the manual commit point, and one event per focus period in which the value changed -
    // never one per keystroke. The dirty bit is set from the OCCURRENCE of a user-edit action for
    // this field, never by comparing values, because no field value may ever enter this module.
    if (period.dirty) {
        emitValueApplied({
            workObjectId: period.workObjectId,
            fieldKey: period.fieldKey,
            fieldType: period.fieldType,
            inputSource: ACTIVITY_INPUT_SOURCE.MANUAL,
            // The source id is omitted for manual entry, per the architect decision of 2026-09-04.
            automationSource: null,
            context: period.context,
        });
    }

    emitFieldEnded(period, endReason);
}

function closeOpenTabPeriod(endReason) {
    if (!currentTabPeriod) return;
    const period = currentTabPeriod;
    currentTabPeriod = null;
    emitTabEnded(period, endReason);
}

function startTabPeriod({ transactionId, pageKey, context }) {
    const workObjectId = String(transactionId);
    // The period id: a separate id carried by the started and the ended event alike. It is NOT the
    // started event's `event_id` - `emit` generates that one on its own.
    const correlationId = nextEventId();

    currentTabPeriod = { correlationId, workObjectId, pageKey: pageKey ?? null, context: normalizeContext(context) };

    emitTabStarted(currentTabPeriod);
}

/**
 * Opens a tab period for `transactionId`, closing whatever was open first.
 *
 * Re-entrant on the same transaction: if that period is already open this is a no-op, so a
 * secondary trigger (a split-view card selection while a field in that card is focused) cannot
 * churn the period. Passing `null` closes the current period and opens nothing, which is what
 * moving to Desks or the Video Wall does - neither holds a work object.
 */
export function switchTo({ transactionId, pageKey, context, endReason = ACTIVITY_END_REASON.CONTEXT_SWITCH } = {}) {
    try {
        if (!isActivityQueueEnabled()) return;

        const workObjectId = transactionId === null || transactionId === undefined ? null : String(transactionId);

        if (workObjectId && currentTabPeriod?.workObjectId === workObjectId) {
            // Already the focused work object. Refresh the captured context so a later close
            // carries the newest operational ids, but do not restart the period.
            if (context) currentTabPeriod.context = normalizeContext(context);
            return;
        }

        // Field period first, so it gets the lower `producer_sequence`.
        closeOpenFieldPeriod(endReason);
        closeOpenTabPeriod(endReason);

        if (workObjectId) startTabPeriod({ transactionId: workObjectId, pageKey, context });
    } catch (error) {
        console.warn('activityTracking: switchTo failed.', error);
    }
}

/**
 * Opens a field period, opening or switching the enclosing tab period first when needed.
 *
 * FOCUS DRIVES THE PERIOD, THE STORE FLAG DOES NOT. In split view every grouped transaction mounts
 * a complete form, so many full forms are focusable at once, and `splitViewActiveTransactionId`
 * does NOT move when the agent tabs with the keyboard from the last field of one card's form into
 * the first field of the next. Deriving the transaction from the form that fired the focus event
 * and switching here is what stops that from producing a field period with no enclosing tab period.
 */
export function openFieldPeriod({ transactionId, fieldKey, fieldType, pageKey, context } = {}) {
    try {
        if (!isActivityQueueEnabled()) return;

        const guardedKey = toSchemaFieldKey(fieldKey);
        // No usable key means no trustworthy `target_id`, so emit nothing rather than a mislabelled
        // period. Null-safe by design: a missing field produces no event, never a throw.
        if (!guardedKey) return;

        const workObjectId = transactionId === null || transactionId === undefined ? null : String(transactionId);
        if (!workObjectId) return;

        // Re-focusing the field that is ALREADY open is a no-op, and this has to be checked before
        // anything is closed: opening the option list of a composite input (autocomplete, select)
        // moves DOM focus and fires focusin again for the same field, and closing plus reopening
        // there would split one interaction into two periods.
        if (currentFieldPeriod?.workObjectId === workObjectId && currentFieldPeriod?.fieldKey === guardedKey) return;

        if (currentTabPeriod?.workObjectId !== workObjectId) {
            switchTo({ transactionId: workObjectId, pageKey, context, endReason: ACTIVITY_END_REASON.CONTEXT_SWITCH });
        } else {
            closeOpenFieldPeriod(ACTIVITY_END_REASON.CONTEXT_SWITCH);
        }

        const correlationId = nextEventId();
        currentFieldPeriod = {
            correlationId,
            workObjectId,
            fieldKey: guardedKey,
            fieldType: fieldType ?? null,
            pageKey: pageKey ?? currentTabPeriod?.pageKey ?? null,
            context: normalizeContext(context ?? currentTabPeriod?.context),
            dirty: false,
        };

        emitFieldStarted(currentFieldPeriod);
    } catch (error) {
        console.warn('activityTracking: openFieldPeriod failed.', error);
    }
}

export function closeFieldPeriod({ endReason = ACTIVITY_END_REASON.CONTEXT_SWITCH } = {}) {
    try {
        if (!isActivityQueueEnabled()) return;
        closeOpenFieldPeriod(endReason);
    } catch (error) {
        console.warn('activityTracking: closeFieldPeriod failed.', error);
    }
}

/** Closes everything open, field period first so it carries the lower `producer_sequence`. */
export function closeAll({ endReason = ACTIVITY_END_REASON.UNKNOWN } = {}) {
    try {
        if (!isActivityQueueEnabled()) return;
        closeOpenFieldPeriod(endReason);
        closeOpenTabPeriod(endReason);
    } catch (error) {
        console.warn('activityTracking: closeAll failed.', error);
    }
}

/**
 * Closes the periods belonging to one transaction, used by the completion seams: a transaction
 * finishing must not close a period the agent has already moved on from.
 */
export function closeTransactionPeriods({ transactionId, endReason = ACTIVITY_END_REASON.UNKNOWN } = {}) {
    try {
        if (!isActivityQueueEnabled()) return;

        const workObjectId = transactionId === null || transactionId === undefined ? null : String(transactionId);
        if (!workObjectId) return;

        if (currentFieldPeriod?.workObjectId === workObjectId) closeOpenFieldPeriod(endReason);
        if (currentTabPeriod?.workObjectId === workObjectId) closeOpenTabPeriod(endReason);
    } catch (error) {
        console.warn('activityTracking: closeTransactionPeriods failed.', error);
    }
}

/**
 * Records that a user-edit action was observed for this field while its period is open.
 *
 * Derived from the OCCURRENCE of the action, never from comparing values: comparing would mean
 * reading values into the tracking module, and the one invariant this feature cannot violate is
 * that no field value ever enters it. An occurrence-based dirty bit needs no value at all.
 *
 * Deliberately NOT the shared `fieldChanged` flag in `fieldControllers.js` - that is one
 * module-level mutable variable for the whole app, not a per-field record.
 */
export function markOpenFieldDirty({ transactionId, fieldKey } = {}) {
    try {
        if (!currentFieldPeriod) return false;

        const workObjectId = transactionId === null || transactionId === undefined ? null : String(transactionId);
        const guardedKey = toSchemaFieldKey(fieldKey);
        if (!workObjectId || !guardedKey) return false;

        if (currentFieldPeriod.workObjectId !== workObjectId || currentFieldPeriod.fieldKey !== guardedKey) return false;

        currentFieldPeriod.dirty = true;
        return true;
    } catch (error) {
        console.warn('activityTracking: markOpenFieldDirty failed.', error);
        return false;
    }
}

/**
 * Emits a standalone automation `field.value.applied`, opening NO period.
 *
 * AN AUTOMATION EVENT MUST NEVER OPEN A FOCUS PERIOD. Automation writes to transactions the agent
 * is not looking at - a new transaction arriving gets autofilled before anyone focuses it. Opening
 * a tab period to "host" the value event would fabricate focus time on an unfocused transaction,
 * and focused time is the headline metric of this whole epic.
 */
export function recordAutomationValueApplied({ transactionId, fieldKey, fieldType, automationSource, context } = {}) {
    try {
        if (!isActivityQueueEnabled()) return;

        const guardedKey = toSchemaFieldKey(fieldKey);
        const workObjectId = transactionId === null || transactionId === undefined ? null : String(transactionId);
        if (!guardedKey || !workObjectId) return;

        emitValueApplied({
            workObjectId,
            fieldKey: guardedKey,
            fieldType: fieldType ?? null,
            inputSource: ACTIVITY_INPUT_SOURCE.AUTOMATION,
            automationSource: automationSource ?? null,
            context,
        });
    } catch (error) {
        console.warn('activityTracking: recordAutomationValueApplied failed.', error);
    }
}

/**
 * Emits a standalone MANUAL `field.value.applied`, opening NO period, for an Agent's change made
 * outside the field itself (the step-by-step protocol's Detected select). A change in the field is
 * reported at blur from the period's dirty bit instead; the caller skips this while that field's period
 * is open, so one change is never reported twice.
 */
export function recordManualValueApplied({ transactionId, fieldKey, fieldType, context } = {}) {
    try {
        if (!isActivityQueueEnabled()) return;

        const guardedKey = toSchemaFieldKey(fieldKey);
        const workObjectId = transactionId === null || transactionId === undefined ? null : String(transactionId);
        if (!guardedKey || !workObjectId) return;

        emitValueApplied({
            workObjectId,
            fieldKey: guardedKey,
            fieldType: fieldType ?? null,
            inputSource: ACTIVITY_INPUT_SOURCE.MANUAL,
            // The source id is omitted for manual entry, as in `closeOpenFieldPeriod`.
            automationSource: null,
            context,
        });
    } catch (error) {
        console.warn('activityTracking: recordManualValueApplied failed.', error);
    }
}

/** Read by the field listener so it can tell a re-focus from a genuine switch. */
export function getOpenTabTransactionId() {
    return currentTabPeriod?.workObjectId ?? null;
}

/** Read by the value-applied seam so a manual write is deferred to blur rather than emitted twice. */
export function hasOpenFieldPeriodFor({ transactionId, fieldKey } = {}) {
    if (!currentFieldPeriod) return false;
    const workObjectId = transactionId === null || transactionId === undefined ? null : String(transactionId);
    return currentFieldPeriod.workObjectId === workObjectId && currentFieldPeriod.fieldKey === toSchemaFieldKey(fieldKey);
}

/** Test seam only: both periods are module state that would otherwise leak between tests. */
export function resetFocusPeriodTracker() {
    currentTabPeriod = null;
    currentFieldPeriod = null;
}
